import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons/faCircleInfo';
import { TypedMemo } from '../../../common/types';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { PromoteContainer } from '../../promote/PromoteContainer';
import { SMLBackLink } from '../../components/SMLBackLink/SMLBackLink';
import { useIsDemoAccount } from '../../../accountSelector/useIsDemoAccount';
import { useOutreachRealtime } from '../hooks/useOutreachRealtime';
import { outreachMessages as messages } from '../outreachMessages';
import { pitchHeadMessages } from '../pitchHeadMessages';
import { pitchTabs, PitchTabScreen } from '../pitchTabs';
import { PitchTabRow } from './PitchTabRow';
import { PitchASongButton } from './PitchASongButton';
import { PitchTabIntro } from './PitchTabIntro';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import styles from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface OutreachShellBase {
  actions?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * A tab of Pitch (Campaigns, Contacts, Find, Community spots): the shared head "Pitch", its line,
 * "Pitch a song" after the page actions, and the tabs (design navigation-v2 d 4a, 4d).
 */
interface OutreachShellTabs extends OutreachShellBase {
  section: 'tabs';
  tab: PitchTabScreen;
  /** The release that "Pitch a song" opens the builder with. */
  pitchSong?: string | null;
  /** A heading of the tab body under the tabs ("Who fits Low Tide"). */
  heading?: string;
  /** A line of the tab body under the tabs. */
  lead?: string;
}

/**
 * A page under Pitch (one campaign, the review, the builder, Your sound, the results): a back link
 * to Pitch, its own title and no Pitch tabs (design navigation-v2 d 4e).
 */
interface OutreachShellUnder extends OutreachShellBase {
  section: 'under';
  title: string;
  subtitle?: string;
  /** The crumbs after "Outreach". Default: the title. */
  crumbs?: string[];
}

/** A menu item with its own head and no tabs: Inbox (design navigation-v2 d 4f). */
interface OutreachShellItem extends OutreachShellBase {
  section: 'item';
  title: string;
  subtitle?: string;
  crumbs?: string[];
}

export type OutreachShellProps = OutreachShellTabs | OutreachShellUnder | OutreachShellItem;

/**
 * The shell of every Outreach page: the page head (the crumb names the Outreach group), the
 * realtime refresh of the `outreach.*` queries and the demo note (design outreach a3 note: the demo
 * artist shows a filled read-only page). `section` picks the head of navigation v2: the Pitch tabs,
 * a page under Pitch, or a menu item (Inbox).
 */
/** Splits the props by the kind of head: the Pitch tabs, or a head of its own. */
function shellHead(props: OutreachShellProps): {
  tabs: OutreachShellTabs | undefined;
  own: OutreachShellUnder | OutreachShellItem | undefined;
} {
  return props.section === 'tabs' ? { tabs: props, own: undefined } : { tabs: undefined, own: props };
}

const OutreachShellComponent: React.FC<OutreachShellProps> = (props) => {
  const intl = useIntl();
  const isDemo = useIsDemoAccount();
  useOutreachRealtime();
  const { actions, children } = props;
  const pitchTitle = intl.formatMessage(pitchHeadMessages.title);
  const { tabs, own } = shellHead(props);
  const tabScreen = tabs?.tab;
  const ownCrumbs = own?.crumbs;

  const crumbs = useMemo(() => {
    if (tabScreen === undefined) {
      return ownCrumbs;
    }
    // "Outreach / Pitch" on Campaigns, "Outreach / Pitch / Contacts" on the other tabs (design 4d).
    const active = pitchTabs(tabScreen).find((tab) => tab.active);
    return active === undefined || active.id === 'campaigns'
      ? [pitchTitle]
      : [pitchTitle, intl.formatMessage(active.label)];
  }, [intl, ownCrumbs, pitchTitle, tabScreen]);

  // A Pitch tab ends its buttons with "Pitch a song" (design 4d: Import, then Pitch a song).
  const pitchButton = tabs === undefined ? null : <PitchASongButton song={tabs.pitchSong ?? null} />;
  const headActions =
    actions === undefined && pitchButton === null ? undefined : (
      <div className={styles.headActions}>
        {actions}
        {pitchButton}
      </div>
    );

  return (
    <PromoteContainer
      title={tabs === undefined ? (own?.title ?? '') : pitchTitle}
      subtitle={tabs === undefined ? own?.subtitle : intl.formatMessage(pitchHeadMessages.subtitle)}
      crumbs={crumbs}
      back={own?.section === 'under' ? <SMLBackLink to={SMLRoutes.outreach} destination={pitchTitle} /> : undefined}
      tabs={tabs === undefined ? undefined : <PitchTabRow screen={tabs.tab} />}
      actions={headActions}
    >
      {isDemo && (
        <div className={styles.alert} data-testid="outreach-demo-note">
          <FontAwesomeIcon icon={faCircleInfo} className={styles.alertIcon} />
          <span>{intl.formatMessage(messages.demoData)}</span>
        </div>
      )}
      {tabs !== undefined && <PitchTabIntro heading={tabs.heading} lead={tabs.lead} />}
      {children}
    </PromoteContainer>
  );
};

export const OutreachShell = TypedMemo(OutreachShellComponent);
